/* eslint-disable camelcase */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  TextField,
  Stack,
  Typography,
  MenuItem,
  CircularProgress,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import isEmail from 'validator/lib/isEmail';
import { toast } from '../components/toast';
import { createNewClient } from '../services/api';
import { USA_STATES } from '../utils/conts';
import { LightTooltip } from '../components/custom-components/custom-tooltip';

const FIELD_LABELS = {
  username: 'Username',
  practice_name: 'Practice name',
  email: 'Primary email',
  secondary_emails: 'Secondary emails',
  phone: 'Phone',
  zipcode: 'ZIP code',
  state: 'State',
  city: 'City',
  address: 'Street address',
};

/** Flatten Formik/Yup errors into { field, message } for a submit summary. */
function collectErrorEntries(errors) {
  const entries = [];
  if (!errors || typeof errors !== 'object') return entries;

  Object.keys(errors).forEach((key) => {
    const val = errors[key];
    const label = FIELD_LABELS[key] || key;

    if (typeof val === 'string' && val.trim()) {
      entries.push({ field: label, message: val });
    } else if (Array.isArray(val)) {
      val.forEach((item, idx) => {
        if (typeof item === 'string' && item.trim()) {
          entries.push({ field: `${label} (${idx + 1})`, message: item });
        }
      });
    }
  });
  return entries;
}

function FormGroup({ title, description, children }) {
  return (
    <Box component="section">
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 720, lineHeight: 1.65 }}>
        {description}
      </Typography>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

const validationSchema = yup.object({
  username: yup
    .string('Enter the username')
    .required('Username is required')
    .max(100, `Username can't be more than 100 characters`)
    .matches(/^[a-z]+$/, 'Username must be lowercase and contain no spaces or special characters'),
  practice_name: yup
    .string('Enter the practice name')
    .required('Practice name is required')
    .max(100, `Practice name can't be more than 100 characters`),
  email: yup
    .string('Enter the email')
    .email('Please enter a valid email address')
    .required('Email is required')
    .test(
      'is-valid',
      () => `Please enter a valid email address`,
      (value) => (value ? isEmail(value) : new yup.ValidationError('Please enter a valid email address'))
    )
    .max(100, `Email can't be more than 100 characters`),
  secondary_emails: yup.array().of(yup.string().email('Invalid email address')),
  phone: yup
    .number('Enter the phone number')
    .required('Phone number is required')
    .test('maxDigits', 'Phone number should have 10 digits', (value) => {
      if (value) {
        return value.toString().length === 10;
      }
      return true;
    }),
  zipcode: yup
    .number('Enter the zip code')
    .required('Zip code is required')
    .test('maxDigits', 'Zip code should have 5 digits', (value) => {
      if (value) {
        return value.toString().length === 5;
      }
      return true;
    }),
  state: yup.string('Enter the state').required('State is required').max(50, `State can't be more than 50 characters`),
  city: yup.string('Enter the city').required('City is required').max(50, `City can't be more than 50 characters`),
  address: yup
    .string('Enter the address')
    .required('Address is required')
    .max(200, `Address can't be more than 200 characters`),
});

export default function NewClientForm() {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();
  const errorSummaryRef = useRef(null);
  const prevSubmitCountRef = useRef(0);

  const formik = useFormik({
    initialValues: {
      username: '',
      practice_name: '',
      email: '',
      secondary_emails: [],
      phone: '',
      zipcode: '',
      state: '',
      city: '',
      address: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await createNewClient(values);
        toast.success('Client account successfully created.');
        setLoading(false);
        navigate('/clients', { replace: true });
      } catch (error) {
        console.error('Error', error);
        if (error.response.status === 409) {
          toast.error('Username or email already exists. Try with different values.');
        } else {
          toast.error('Error creating client account. Try again later.');
        }
        setLoading(false);
      }
    },
  });
  const handleFieldBlur = (fieldName) => {
    formik.setFieldTouched(fieldName, true, true);
  };

  const handleAddSecondaryEmail = () => {
    const email = inputValue.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!formik.values.secondary_emails.includes(email)) {
        formik.setFieldValue('secondary_emails', [...formik.values.secondary_emails, email]);
        setInputValue('');
      }
    } else if (email) {
      formik.setFieldTouched('secondary_emails', true);
      formik.setFieldError('secondary_emails', 'Invalid email address');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      handleAddSecondaryEmail();
    }
  };

  const handleRemoveSecondaryEmail = (index) => {
    const updated = [...formik.values.secondary_emails];
    updated.splice(index, 1);
    formik.setFieldValue('secondary_emails', updated);
  };

  const errorSummaryItems = useMemo(
    () => (formik.submitCount > 0 ? collectErrorEntries(formik.errors) : []),
    [formik.submitCount, formik.errors]
  );

  useEffect(() => {
    if (formik.submitCount <= prevSubmitCountRef.current) return;
    prevSubmitCountRef.current = formik.submitCount;
    if (Object.keys(formik.errors || {}).length > 0) {
      queueMicrotask(() => {
        errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [formik.submitCount, formik.errors]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          Username must be <strong>lowercase letters only</strong> (no spaces). Phone and ZIP must be{' '}
          <strong>10</strong> and <strong>5</strong> digits. Errors appear under each field after you leave it or submit.
        </Alert>

        {errorSummaryItems.length > 0 && (
          <Alert
            ref={errorSummaryRef}
            id="new-client-form-error-summary"
            severity="error"
            variant="outlined"
            role="alert"
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom component="p" sx={{ m: 0 }}>
              Fix the issues below, then try again
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Fields with problems are also marked in the form above.
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25, listStyleType: 'disc' }}>
              {errorSummaryItems.map((item, i) => (
                <Typography key={`${item.field}-${item.message}-${i}`} component="li" variant="body2">
                  <strong>{item.field}</strong>
                  {' — '}
                  {item.message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
            boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1],
          }}
        >
          <CardContent sx={{ '&:last-child': { pb: 3 } }}>
            <Stack spacing={4} divider={<Divider flexItem />}>
              <FormGroup
                title="Account & practice"
                description="The username is the client's portal login. Practice name is how this organization appears in your directory and on documents."
              >
                <TextField
                  id="username"
                  name="username"
                  label="Username"
                  fullWidth
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('username')}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={
                    (formik.touched.username && formik.errors.username) ||
                    'Lowercase letters only — used as their login ID.'
                  }
                />
                <TextField
                  id="practice_name"
                  name="practice_name"
                  label="Practice name"
                  fullWidth
                  value={formik.values.practice_name}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('practice_name')}
                  error={formik.touched.practice_name && Boolean(formik.errors.practice_name)}
                  helperText={formik.touched.practice_name && formik.errors.practice_name}
                />
              </FormGroup>

              <FormGroup
                title="Contact"
                description="Primary email receives onboarding and credentials. Add secondary contacts if billing or operations should get copies."
              >
                <TextField
                  id="email"
                  name="email"
                  label="Primary email"
                  type="email"
                  fullWidth
                  autoComplete="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('email')}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <TextField
                  label="Secondary emails (optional)"
                  variant="outlined"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => formik.setFieldTouched('secondary_emails', true)}
                  error={formik.touched.secondary_emails && Boolean(formik.errors.secondary_emails)}
                  helperText={
                    (formik.touched.secondary_emails && formik.errors.secondary_emails) ||
                    'Press Enter, comma, or space after each address to add it.'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LightTooltip title="Press Enter, comma, or space to add the email in the field as a chip.">
                          <IconButton edge="end" size="small" aria-label="How to add secondary emails">
                            <KeyboardReturnIcon sx={{ color: 'action.active' }} fontSize="small" />
                          </IconButton>
                        </LightTooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formik.values.secondary_emails.map((email, index) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveSecondaryEmail(index)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </FormGroup>

              <FormGroup
                title="Practice location"
                description="Use the practice's main phone and mailing address. These fields support contracts and outreach."
              >
                <TextField
                  id="phone"
                  name="phone"
                  label="Phone"
                  type="tel"
                  fullWidth
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('phone')}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={(formik.touched.phone && formik.errors.phone) || '10-digit US number, no symbols.'}
                />
                <TextField
                  id="zipcode"
                  name="zipcode"
                  label="ZIP code"
                  fullWidth
                  inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                  value={formik.values.zipcode}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('zipcode')}
                  error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
                  helperText={(formik.touched.zipcode && formik.errors.zipcode) || '5 digits.'}
                />
                <TextField
                  id="state"
                  name="state"
                  label="State"
                  select
                  fullWidth
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('state')}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                >
                  {USA_STATES.map((state) => (
                    <MenuItem key={state.abbreviation} value={state.abbreviation}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  id="city"
                  name="city"
                  label="City"
                  fullWidth
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('city')}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
                <TextField
                  id="address"
                  name="address"
                  label="Street address"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={() => handleFieldBlur('address')}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </FormGroup>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" flexWrap="wrap" sx={{ mb: 5 }}>
          <LightTooltip title="Creates the account and sends onboarding details to the primary email when successful.">
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              loadingPosition="start"
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ backgroundColor: '#0A9EF2', px: 3 }}
            >
              Create client account
            </LoadingButton>
          </LightTooltip>
          <Button variant="outlined" sx={{ color: '#0A9EF2', borderColor: '#0A9EF2' }} onClick={() => navigate('/clients')}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
